import { z } from 'zod';

import { apiRequest, apiRequestBlob, apiStream, type StreamOptions } from '@/api/client';
import {
  storefrontFeaturesSchema,
  type StorefrontFeatures,
} from '@/features/storefront/api/storefront';
import {
  themeButtonsSchema,
  themeColorsSchema,
  themeHeaderSchema,
  themeLayoutSchema,
  themeSchema,
  themeTypographySchema,
  type Theme,
} from '@/features/storefront/themeSchema';
import type { Translator } from '@/lib/i18n';

/**
 * Schemas mirroring `app/schemas/store.py`.
 *
 * The theme document itself is defined once in
 * `features/storefront/themeSchema.ts` and re-exported here, so the dashboard
 * edits exactly what the storefront wears. The theme is data, not markup: the
 * backend validates the whole document on write, so this module sends complete
 * themes rather than partial patches.
 */
export {
  themeButtonsSchema,
  themeColorsSchema,
  themeHeaderSchema,
  themeLayoutSchema,
  themeSchema,
  themeTypographySchema,
};
export type {
  LayoutFamily,
  Theme,
  ThemeColors,
  ThemeHeader,
  ThemeLayout,
} from '@/features/storefront/themeSchema';

// --- Checkout options -------------------------------------------------------

/**
 * The ways a shopper can pay, in the order the storefront offers them.
 *
 * Defined here rather than imported from the storefront feature: the dashboard
 * is what writes this document, and the storefront only reads it.
 */
export const CHECKOUT_METHODS = ['RAZORPAY', 'COD', 'WHATSAPP'] as const;
export type CheckoutMethod = (typeof CHECKOUT_METHODS)[number];

export const CHECKOUT_METHOD_LABELS: Record<CheckoutMethod, string> = {
  RAZORPAY: 'Online payment (Razorpay)',
  COD: 'Cash on delivery',
  WHATSAPP: 'Order via WhatsApp',
};

/** `CHECKOUT_METHOD_LABELS` in the dashboard's language. */
export function checkoutMethodLabel(t: Translator, method: CheckoutMethod): string {
  return t(`store.api.checkout_method.${method}`);
}

/**
 * Mirrors `checkout_options` on `app/schemas/store.py`.
 *
 * Every field carries a default so a response from a server that predates the
 * document - or one that sends a partial one - still parses into a complete
 * set of options. Money is a decimal string, like every other money field.
 */
export const checkoutOptionsSchema = z.object({
  order: z.array(z.enum(CHECKOUT_METHODS)).default([...CHECKOUT_METHODS]),
  cod: z
    .object({
      enabled: z.boolean().default(true),
      fee: z.string().default('0.00'),
      min_order_amount: z.string().nullable().default(null),
      max_order_amount: z.string().nullable().default(null),
      instructions: z.string().nullable().default(null),
    })
    .default({}),
  razorpay: z
    .object({
      enabled: z.boolean().default(true),
      instructions: z.string().nullable().default(null),
    })
    .default({}),
  whatsapp: z
    .object({
      enabled: z.boolean().default(false),
      phone: z.string().nullable().default(null),
      instructions: z.string().nullable().default(null),
      greeting: z.string().nullable().default(null),
    })
    .default({}),
});

export type CheckoutOptions = z.infer<typeof checkoutOptionsSchema>;

/** The statuses a store may let shoppers cancel from, in lifecycle order. */
export const SHOPPER_CANCELLABLE_STATUSES = ['PENDING', 'CONFIRMED', 'PROCESSING', 'PACKED'] as const;
export type ShopperCancellableStatus = (typeof SHOPPER_CANCELLABLE_STATUSES)[number];

export const REFUND_METHODS = ['ORIGINAL_PAYMENT', 'MANUAL', 'STORE_CREDIT'] as const;
export type RefundMethod = (typeof REFUND_METHODS)[number];

/**
 * Mirrors `order_policy` on `app/schemas/store.py`: cancellations, returns,
 * refunds and replacements. Defaults match the server's, so an empty document
 * parses to "shoppers cancel pending or confirmed orders, nothing else".
 */
export const orderPolicySchema = z.object({
  customer_cancellation: z.boolean().default(true),
  cancellable_statuses: z.array(z.enum(SHOPPER_CANCELLABLE_STATUSES)).default(['PENDING', 'CONFIRMED']),
  partial_cancellation: z.boolean().default(false),
  returns: z.boolean().default(false),
  refunds: z.boolean().default(false),
  replacements: z.boolean().default(false),
  request_window_days: z.number().default(7),
  refund_method: z.enum(REFUND_METHODS).default('ORIGINAL_PAYMENT'),
  auto_approve: z.boolean().default(false),
  allow_photos: z.boolean().default(true),
  cancellation_reasons: z.array(z.string()).default([]),
  return_reasons: z.array(z.string()).default([]),
  policy_note: z.string().nullable().default(null),
});

export type OrderPolicy = z.infer<typeof orderPolicySchema>;

/**
 * Mirrors `booking_notifications` on `app/schemas/store.py`: appointment
 * reminders - when they go out and on which channels. Defaults match the
 * server's: one reminder a day before, by email and on WhatsApp.
 */
export const bookingNotificationsSchema = z.object({
  /** Hours before the appointment; null sends no reminders (1..168). */
  reminder_hours_before: z.number().nullable().default(24),
  /** A second, closer reminder; null is none (1..48, below the first). */
  second_reminder_hours_before: z.number().nullable().default(null),
  email: z.boolean().default(true),
  whatsapp: z.boolean().default(true),
  /** OPD queue: how many tokens ahead a patient is told "your turn is near" (1..10). */
  token_near_count: z.number().default(3),
});

export type BookingNotifications = z.infer<typeof bookingNotificationsSchema>;

export const storeSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  subdomain: z.string(),
  custom_domain: z.string().nullable(),
  logo_url: z.string().nullable(),
  favicon_url: z.string().nullable(),
  currency: z.string(),
  timezone: z.string(),
  seo_title: z.string().nullable(),
  seo_description: z.string().nullable(),
  theme_config: themeSchema,
  storefront_features: storefrontFeaturesSchema.default({}),
  checkout_options: checkoutOptionsSchema.default({}),
  order_policy: orderPolicySchema.default({}),
  booking_notifications: bookingNotificationsSchema.default({}),
  status: z.enum(['DRAFT', 'LIVE', 'PAUSED']),
  created_at: z.string(),
});

export const themePresetSchema = z.object({
  key: z.string(),
  name: z.string(),
  description: z.string(),
  industries: z.array(z.string()),
  colors: themeColorsSchema,
  typography: themeTypographySchema,
  buttons: themeButtonsSchema,
  layout: themeLayoutSchema,
  // The page the preset proposes, so a preview shows the theme's own shop
  // rather than every theme's shop looking like a bare product grid. Loose
  // here for the same reason `themeSchema.sections` is - sections are read
  // through `sections/schema.ts`, which skips one it cannot render.
  sections: z.array(z.record(z.unknown())).default([]),
});

/** One theme in the store's library: the active look (MAIN) or a draft. */
export const storeThemeSchema = z.object({
  id: z.string(),
  name: z.string(),
  role: z.enum(['MAIN', 'DRAFT']),
  config: themeSchema,
  created_at: z.string(),
  updated_at: z.string(),
});

export type Store = z.infer<typeof storeSchema>;
export type ThemePreset = z.infer<typeof themePresetSchema>;
export type StoreTheme = z.infer<typeof storeThemeSchema>;

export const uploadedImageSchema = z.object({
  url: z.string(),
  key: z.string(),
  content_type: z.string(),
  size_bytes: z.number(),
});

/**
 * Uploads a logo, favicon or section image to the platform's bucket.
 *
 * Its own endpoint rather than the product one: this answers to store
 * permissions, and whoever designs the storefront is not necessarily allowed to
 * create products.
 */
export function uploadStoreImage(file: File) {
  const body = new FormData();
  body.append('file', file);
  return apiRequest('/media/store-images', uploadedImageSchema, { method: 'POST', body });
}

/**
 * Uploads a video for a theme's video section.
 *
 * The same permissions as the image upload and a different endpoint, because
 * the server checks a video against a different set of formats and a much
 * larger size limit. The response shape is the image one minus the renditions,
 * which a video does not have - nothing transcodes.
 */
export function uploadStoreVideo(file: File) {
  const body = new FormData();
  body.append('file', file);
  return apiRequest('/media/store-videos', uploadedImageSchema, { method: 'POST', body });
}

export function fetchMyStore() {
  return apiRequest('/stores/me', storeSchema);
}

/**
 * Creates the storefront a business does not have yet.
 *
 * `subdomain` is optional: the server falls back to the business slug, which is
 * what the form offers as its prefilled suggestion. `theme_preset` seeds the
 * library's MAIN theme, so a new store is never themeless.
 */
export function createStore(payload: {
  name: string;
  subdomain?: string;
  description?: string;
  theme_preset?: string;
}) {
  return apiRequest('/stores', storeSchema, { method: 'POST', body: payload });
}

/**
 * The theme catalog.
 *
 * `storeName` is spliced into each preset's hero copy by the server, so a
 * merchant browsing or previewing sees their own shop's name rather than a
 * placeholder they have to imagine past.
 */
export function fetchThemePresets(storeName?: string) {
  const query = storeName ? `?store_name=${encodeURIComponent(storeName)}` : '';
  return apiRequest(`/stores/themes${query}`, z.array(themePresetSchema), { auth: 'none' });
}

export function updateStore(payload: {
  name?: string;
  description?: string;
  logo_url?: string;
  favicon_url?: string;
  seo_title?: string;
  seo_description?: string;
  status?: 'DRAFT' | 'LIVE' | 'PAUSED';
  /** Replaced whole, never merged - send the complete document. */
  storefront_features?: StorefrontFeatures;
  /** Also replaced whole: the storefront reads the document, not a diff of it. */
  checkout_options?: CheckoutOptions;
  /** Replaced whole as well. */
  order_policy?: OrderPolicy;
  /** Replaced whole, like the others. */
  booking_notifications?: BookingNotifications;
}) {
  return apiRequest('/stores/me', storeSchema, { method: 'PATCH', body: payload });
}

// --- Store address (subdomain) ----------------------------------------------

/** A subdomain the store gave up, which still redirects to it. */
export const subdomainAliasSchema = z.object({
  subdomain: z.string(),
  created_at: z.string(),
});

/**
 * Where the store lives and whether the merchant may move it. Mirrors
 * `SubdomainPolicyResponse`; the platform's merchant page reads the same shape.
 */
export const subdomainPolicySchema = z.object({
  subdomain: z.string(),
  host: z.string(),
  cooldown_days: z.number(),
  max_renames: z.number(),
  renames_used: z.number(),
  renames_remaining: z.number(),
  last_changed_at: z.string().nullable(),
  next_allowed_at: z.string().nullable(),
  can_rename: z.boolean(),
  blocked_reason: z.enum(['DISABLED', 'LIMIT_REACHED', 'TOO_SOON']).nullable(),
  aliases: z.array(subdomainAliasSchema),
});
export type SubdomainPolicy = z.infer<typeof subdomainPolicySchema>;

export const subdomainAvailabilitySchema = z.object({
  subdomain: z.string(),
  available: z.boolean(),
  reason: z.enum(['INVALID', 'RESERVED', 'TAKEN', 'CURRENT']).nullable(),
});
export type SubdomainAvailability = z.infer<typeof subdomainAvailabilitySchema>;

export function fetchSubdomainPolicy() {
  return apiRequest('/stores/me/subdomain', subdomainPolicySchema);
}

export function checkSubdomainAvailability(name: string) {
  return apiRequest(
    `/stores/me/subdomain/availability?name=${encodeURIComponent(name)}`,
    subdomainAvailabilitySchema,
  );
}

/** Moves the store; the old address keeps redirecting to it. */
export function renameSubdomain(subdomain: string) {
  return apiRequest('/stores/me/subdomain', storeSchema, {
    method: 'PUT',
    body: { subdomain },
  });
}

// --- Store email sender ------------------------------------------------------

/**
 * Who the store's email to shoppers comes from: `"Acme Store"
 * <acme@mail.neoncommerce.in>` on the platform sending domain, with replies
 * going to the support address. Mirrors `StoreSenderResponse`.
 */
export const storeSenderSchema = z.object({
  effective_sender: z.string(),
  sender_address: z.string(),
  sender_domain: z.string(),
  local_part: z.string(),
  /** The subdomain: what `local_part` goes back to on reset. */
  default_local_part: z.string(),
  is_custom: z.boolean(),
  support_email: z.string().nullable(),
  /** Where replies actually go - the support address, else the business email. */
  reply_to: z.string().nullable(),
  /** A connected default mailbox that sends instead, when there is one. */
  overridden_by_mailbox: z.string().nullable(),
  /** Whether this installation sends store mail from the sending domain yet. */
  enabled: z.boolean(),
});
export type StoreSender = z.infer<typeof storeSenderSchema>;

export function fetchStoreSender() {
  return apiRequest('/stores/me/sender', storeSenderSchema);
}

/** Both fields together; `null` resets to the subdomain or the business email. */
export function updateStoreSender(payload: {
  sender_local_part: string | null;
  support_email: string | null;
}) {
  return apiRequest('/stores/me/sender', storeSenderSchema, { method: 'PUT', body: payload });
}

/** Replaces the whole theme document - send what you want the store to have. */
export function saveTheme(theme: Theme) {
  return apiRequest('/stores/me/theme', storeSchema, { method: 'PUT', body: theme });
}

// --- Custom domains --------------------------------------------------------

export const DOMAIN_STATUSES = ['PENDING', 'VERIFIED', 'FAILED', 'RELEASED'] as const;
export type DomainStatus = (typeof DOMAIN_STATUSES)[number];

export const CERTIFICATE_STATUSES = ['NOT_REQUESTED', 'PENDING', 'ISSUED', 'FAILED'] as const;
export type CertificateStatus = (typeof CERTIFICATE_STATUSES)[number];

export const dnsRecordSchema = z.object({
  type: z.enum(['TXT', 'CNAME']),
  name: z.string(),
  value: z.string(),
  purpose: z.string(),
});

export const domainSchema = z.object({
  id: z.string(),
  domain: z.string(),
  status: z.enum(DOMAIN_STATUSES),
  verified_at: z.string().nullable(),
  // Ownership, routing and certificate are three separate truths. A single
  // "connected" flag is what produces a green tick above a shop that will not
  // load, so the API reports them apart and so does the UI.
  cname_ok: z.boolean(),
  certificate_status: z.enum(CERTIFICATE_STATUSES),
  certificate_detail: z.string().nullable(),
  last_error: z.string().nullable(),
  last_checked_at: z.string().nullable(),
  check_attempts: z.number(),
  created_at: z.string(),
  records: z.array(dnsRecordSchema),
  live: z.boolean(),
});

export type DnsRecord = z.infer<typeof dnsRecordSchema>;
export type StoreDomain = z.infer<typeof domainSchema>;

export function fetchMyDomains() {
  return apiRequest('/stores/me/domains', z.array(domainSchema));
}

/** Opens a claim and returns the DNS records that prove it. Serves nothing yet. */
export function requestDomain(domain: string) {
  return apiRequest('/stores/me/domains', domainSchema, { method: 'POST', body: { domain } });
}

/** Re-reads DNS. Safe to call repeatedly. */
export function verifyDomain(domainId: string) {
  return apiRequest(`/stores/me/domains/${domainId}/verify`, domainSchema, { method: 'POST' });
}

export function disconnectDomain(domainId: string) {
  return apiRequest(`/stores/me/domains/${domainId}`, domainSchema, { method: 'DELETE' });
}

// --- Theme library --------------------------------------------------------
export function fetchMyThemes() {
  return apiRequest('/stores/me/themes', z.array(storeThemeSchema));
}

/**
 * Adds a catalog preset and makes it live in one step.
 *
 * For a merchant who has just previewed the theme on their own shop and said
 * yes. Their products, categories, prices, stock, orders and customers are not
 * part of a theme document and are untouched; `keepSections` decides the one
 * thing that genuinely could be lost, which is the home page they had built.
 * The theme that was live stays in the library either way.
 */
export function applyThemePreset(
  preset: string,
  options: { keepSections?: boolean; name?: string } = {},
) {
  return apiRequest('/stores/me/themes/apply', storeThemeSchema, {
    method: 'POST',
    body: { preset, name: options.name, keep_sections: options.keepSections ?? false },
  });
}

/** Adds a catalog preset to the library as a draft; publishing is separate. */
export function addThemeFromPreset(preset: string, name?: string) {
  return apiRequest('/stores/me/themes', storeThemeSchema, {
    method: 'POST',
    body: { preset, name },
  });
}

/**
 * Adds a whole theme document to the library as a draft.
 *
 * The document half of `addThemeFromPreset`. A theme that lives in the
 * frontend as a package has no preset key on the server - what the merchant
 * previewed *is* the document - so the document is what gets sent. It lands as
 * a draft either way: nothing here touches the live storefront.
 */
export function addThemeDocument(config: Theme, name?: string) {
  return apiRequest('/stores/me/themes', storeThemeSchema, {
    method: 'POST',
    body: { config, name },
  });
}

/** Describe the business; the AI drafts a theme into the library. */
export function generateTheme(description: string) {
  return apiRequest('/stores/me/themes/generate', storeThemeSchema, {
    method: 'POST',
    body: { description },
  });
}

/** A gallery theme as the AI is offered it: what the merchant's gallery calls it. */
export interface ThemeChoice {
  key: string;
  name: string;
  hint: string;
  industries: string[];
}

/**
 * What the AI answers when it is offered the gallery: which theme to start
 * from, and the merchant's own name, accent and welcome banner to lay over it.
 */
export const themeProposalSchema = z.object({
  base_theme: z.string().nullable(),
  name: z.string(),
  colors: z.object({ primary: z.string() }).passthrough(),
  hero_heading: z.string(),
  hero_subheading: z.string().nullable().optional(),
  hero_cta_label: z.string().nullable().optional(),
});
export type ThemeProposal = z.infer<typeof themeProposalSchema>;

/**
 * A theme designed from a description, streamed: `stage` and `color` events
 * as the AI designs it, `delta` events for its name and hero copy.
 *
 * Offered the gallery, the AI starts from one of its themes and the stream
 * resolves with a proposal, saving nothing - the dashboard builds that theme
 * and saves it (see `aiThemeDocument`).
 */
export function streamThemeGeneration(
  description: string,
  themes: readonly ThemeChoice[],
  options: Pick<StreamOptions, 'signal' | 'onEvent'> = {},
) {
  return apiStream('/stores/me/themes/generate/stream', themeProposalSchema, {
    method: 'POST',
    body: { description, themes },
    ...options,
  });
}

// "Ask for changes" moved to `./assist.ts`, with its streamed route and the
// conversation history; re-exported here so existing imports keep working.
export {
  askForThemeChanges,
  themeAssistResultSchema,
  type ThemeAssistRequest,
  type ThemeAssistResult,
} from '@/features/store/api/assist';

/** Rename and/or replace the config. Config changes to the active theme go live. */
export function updateStoreTheme(id: string, payload: { name?: string; config?: Theme }) {
  return apiRequest(`/stores/me/themes/${id}`, storeThemeSchema, {
    method: 'PATCH',
    body: payload,
  });
}

export function publishTheme(id: string) {
  return apiRequest(`/stores/me/themes/${id}/publish`, storeThemeSchema, { method: 'POST' });
}

export function duplicateTheme(id: string) {
  return apiRequest(`/stores/me/themes/${id}/duplicate`, storeThemeSchema, { method: 'POST' });
}

export function deleteTheme(id: string) {
  return apiRequest(`/stores/me/themes/${id}`, z.record(z.unknown()), { method: 'DELETE' });
}

// --- Storefront QR code -----------------------------------------------------

/**
 * Mirrors `StoreQrCode` on `app/schemas/store.py`.
 *
 * Both renderings arrive together and neither is optional: the SVG is what the
 * screen and the printer want, the PNG is what a share sheet wants, and a
 * merchant who taps Share should not be waiting on a second request while the
 * sheet is already open.
 */
export const storeQrCodeSchema = z.object({
  url: z.string(),
  storefront_url: z.string(),
  store_name: z.string(),
  subdomain: z.string(),
  custom_domain: z.string().nullable(),
  svg: z.string(),
  png_data_url: z.string(),
  png_size_px: z.number(),
});

export type StoreQrCode = z.infer<typeof storeQrCodeSchema>;

/**
 * The code for *your* storefront. There is no other store this can name - the
 * server takes it from the session, exactly as `/stores/me` does.
 */
/** `PushSettings` in `app/schemas/push.py`: the shop's switch, and what else it needs. */
export const pushSettingsSchema = z.object({
  enabled: z.boolean(),
  /** The platform has push set up at all. */
  configured: z.boolean(),
  /** The plan includes push and the installable shop it arrives through. */
  included: z.boolean(),
  subscribers: z.number(),
});

export type PushSettings = z.infer<typeof pushSettingsSchema>;

export function fetchPushSettings() {
  return apiRequest('/push-notifications/settings', pushSettingsSchema);
}

export function updatePushSettings(enabled: boolean) {
  return apiRequest('/push-notifications/settings', pushSettingsSchema, {
    method: 'PUT',
    body: { enabled },
  });
}

export function fetchStoreQrCode() {
  return apiRequest('/stores/me/qr-code', storeQrCodeSchema);
}

/** The same code as a file, for a merchant who wants to keep or upload one. */
export function downloadStoreQrCodePng(size: number) {
  return apiRequestBlob(`/stores/me/qr-code.png?size=${size}`);
}
